import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as CloseIcon } from 'assets/iconButtons/new-close-icon.svg';
import { ReactComponent as CardPurchaseIcon } from 'assets/iconButtons/new-card-purchase-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/iconButtons/new-copy-icon.svg';
import { ReactComponent as ExportIcon } from 'assets/iconButtons/new-export-icon.svg';
import { ReactComponent as RepeatIcon } from 'assets/iconButtons/new-repeat-icon.svg';
import {
  CUSTOMER_PERMISSIONS,
  PAYMENT_METHOD,
  PAYMENT_PROVIDERS,
  TRANSACTION_TYPES,
  TYPES_OF_TRANSACTION
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { useMediaQuery } from 'services/useMediaQUery';
import { ROUTE_PATHS } from 'routes/constants';
import { MEDIA_QUERY } from 'services/utils';
import { amountFormattedValue, formatPaymentDateSeparate } from 'services/utils';
import Button from 'uikit/Button/Button';
import './CardPaymentDetails.scss';
import { filteringPaymentMethods } from '../../../PaymentForm/utils';

const CardPaymentDetails = ({
  transaction,
  amountDetails,
  commissionDetails,
  totalAmountDetails,
  onClose,
  accountNumber,
  userAccounts,
  paymentStore,
  transactionStore,
  customerPermissions,
  userStore
}) => {
  const navigate = useNavigate();
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const [copiedField, setCopiedField] = useState(null);

  const isReportDisabled = customerPermissions
    ? !customerPermissions?.find((permission) => permission === CUSTOMER_PERMISSIONS.CREATE_REPORT)
    : false;

  // Calculate amounts and statuses
  const isOutgoingTransaction = transaction.type === TRANSACTION_TYPES.OUTGOING;
  const isIncomingTransaction = transaction.type === TRANSACTION_TYPES.INCOMING;
  const isFailedOrRejected = transaction.status === 'FAILED' || transaction.status === 'REJECTED';
  const displayStatus = transaction.status === 'PAID' ? 'COMPLETED' : transaction.status;

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const repeatTransaction = () => {
    const senderAccount = userAccounts.find((userAccount) => userAccount.wallet_number === transaction.wallet_number);

    paymentStore.resetPaymentStore();
    paymentStore.setSelectedAccount(senderAccount);
    paymentStore.setPreviousTransactionInfo(transaction);
    paymentStore.setIsRepeatPaymentStatus(true);
    paymentStore.setPaymentMethod(
      senderAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
        ? PAYMENT_METHOD.CRYPTO
        : transaction.payment_method
    );
    paymentStore.setAvailablePaymentMethods(
      filteringPaymentMethods(senderAccount?.payment_methods, senderAccount?.transfer_provider)
    );
    navigate(ROUTE_PATHS.NEW_PAYMENT);
  };

  const onPDFClick = (e) => {
    e.preventDefault();
    if (transaction?.is_pdf_available) {
      transactionStore.downloadCurrentPDF(transaction.account_number, transaction.transaction_number);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClassMap = {
      COMPLETED: 'status-completed',
      PAID: 'status-completed',
      IN_PROGRESS: 'status-in-progress',
      PROCESSING: 'status-in-progress',
      PENDING: 'status-pending',
      ON_HOLD: 'status-on-hold',
      FAILED: 'status-failed',
      CANCELLED: 'status-cancelled',
      REQUESTED: 'status-requested',
      MONITORING: 'status-monitoring'
    };
    return statusClassMap[status] || 'status-pending';
  };

  const baseAmount = amountFormattedValue({
    currency: transaction.currency,
    value: transaction.amount
  });
  const amount = `${isOutgoingTransaction ? `-${transaction?.amountDetails?.displayAmount}` : isIncomingTransaction ? `+${transaction?.sourceAmountDetails?.displayAmount || transaction?.amountDetails?.displayAmount}` : ''}`;

  const commission = commissionDetails
    ? amountFormattedValue({
        currency: commissionDetails.currency,
        value: commissionDetails.value
      })
    : amountFormattedValue({
        currency: transaction.currency,
        value: transaction.commission || 0
      });

  const createdDate = formatPaymentDateSeparate(transaction.created_at);
  const completedDate = formatPaymentDateSeparate(transaction.completed_at);
  const lastUpdatedDate = formatPaymentDateSeparate(transaction.updated_at);

  // Format payment purpose with key-value display
  const getFormattedPurpose = () => {
    if (!transaction.purpose || !userStore?.paymentPurposes) {
      return transaction.purpose;
    }
    const purposeValue = userStore.paymentPurposes[transaction.purpose];
    return purposeValue ? `${transaction.purpose} - ${purposeValue}` : transaction.purpose;
  };
  const formattedPurpose = getFormattedPurpose();

  const renderMobile = () => (
    <div className='card-payment-details mobile'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body mobile'>
        {/* Payment Summary */}
        <div className='payment-summary'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                <CardPurchaseIcon className='type-icon' />
              </div>
              <span className='type-label'>{i18n.getMessage('transaction.details.type.PURCHASE')}</span>
            </div>
            <div className='transaction-date'>{`${createdDate.date}, ${createdDate.time}`}</div>
          </div>

          <div className='amount-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className='details-section mobile'>
          <h3 className='section-title'>{i18n.getMessage('transaction.section.transactionDetails')}</h3>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.reference')}</span>
            <div className='detail-value'>
              <span>{transaction.comment}</span>
              <button className='copy-button' onClick={() => handleCopy(transaction.comment, 'reference')}>
                <CopyIcon />
              </button>
            </div>
          </div>

          {transaction.purpose && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
              <div className='detail-value'>
                <span>{formattedPurpose}</span>
                <button className='copy-button' onClick={() => handleCopy(formattedPurpose, 'purpose')}>
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
            <div className='detail-value'>
              <span>{commission}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
            <div className='detail-value'>
              <span>{transaction.transaction_number}</span>
              <button
                className='copy-button'
                onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
              >
                <CopyIcon />
              </button>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
            <div className='detail-value'>
              <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
            <div className='detail-value'>
              <span>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          {completedDate.date && completedDate.time && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
              <div className='detail-value'>
                <span>{`${completedDate.date}, ${completedDate.time}`}</span>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
            <div className='detail-value'>
              <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
            </div>
          </div>
        </div>

        {/* First Section - Sender for Incoming, Recipient for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
            )}
          </h3>

          {isOutgoingTransaction ? (
            // OUTGOING: Show counterparty recipient name
            transaction.recipient_name && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                <div className='detail-value'>
                  <span>{transaction.recipient_name}</span>
                  <button
                    className='copy-button'
                    onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>
            )
          ) : (
            // INCOMING: Show sender name and account
            <>
              {transaction.sender_name && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                  <div className='detail-value'>
                    <span>{transaction.sender_name}</span>
                    <button className='copy-button' onClick={() => handleCopy(transaction.sender_name, 'senderName')}>
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}
              {(transaction.counterparty?.account_number || transaction.sender_wallet_number) && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                  <div className='detail-value'>
                    <span>{transaction.counterparty?.account_number || transaction.sender_wallet_number}</span>
                    <button
                      className='copy-button'
                      onClick={() =>
                        handleCopy(
                          transaction.counterparty?.account_number || transaction.sender_wallet_number,
                          'senderAccount'
                        )
                      }
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
            )}
          </h3>

          {isOutgoingTransaction ? (
            // OUTGOING: Show own account details
            <>
              {(transaction.iban || transaction.wallet_number) && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                  <div className='detail-value'>
                    <span>Card account ({transaction.iban || transaction.wallet_number})</span>
                    <button
                      className='copy-button'
                      onClick={() => handleCopy(transaction.iban || transaction.wallet_number, 'senderAccount')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}

              {transaction.sender_name && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                  <div className='detail-value'>
                    <span>{transaction.sender_name}</span>
                    <button className='copy-button' onClick={() => handleCopy(transaction.sender_name, 'senderName')}>
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            // INCOMING: Show own account details (recipient)
            <>
              {(transaction.iban || transaction.wallet_number) && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                  <div className='detail-value'>
                    <span>{transaction.iban || transaction.wallet_number}</span>
                    <button
                      className='copy-button'
                      onClick={() => handleCopy(transaction.iban || transaction.wallet_number, 'recipientAccount')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}
              {transaction.recipient_name && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                  <div className='detail-value'>
                    <span>{transaction.recipient_name}</span>
                    <button
                      className='copy-button'
                      onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className='action-buttons mobile'>
          <Button
            type='secondary'
            size='small'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button mobile'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.pdf')}
          </Button>

          {transaction.transaction_type === TYPES_OF_TRANSACTION.NORMAL &&
            transaction.type === TRANSACTION_TYPES.OUTGOING && (
              <Button type='primary' size='small' onClick={repeatTransaction} className='repeat-button mobile'>
                <RepeatIcon className='button-icon' />
                {i18n.getMessage('button.repeatPayment')}
              </Button>
            )}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return renderMobile();
  }

  return (
    <div className='card-payment-details'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body'>
        {/* Transaction Header */}
        <div className='transaction-header'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                <CardPurchaseIcon className='type-icon' />
              </div>
              <div className='type-info'>
                <span className='type-label'>{i18n.getMessage('transaction.details.type.PURCHASE')}</span>
              </div>
            </div>

            <div className='transaction-type'>
              <span className='type-date'>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          <div className='transaction-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        <div className='details-container'>
          {/* Transaction Details Section */}
          <div className='details-section transaction-details-section'>
            <div className='section-header'>
              <h3>{i18n.getMessage('transaction.section.transactionDetails')}</h3>
            </div>
            <div className='section-content'>
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.reference')}</span>
                <div className='detail-value with-action'>
                  <span>{transaction.comment}</span>
                  <button
                    className='copy-button'
                    onClick={() => handleCopy(transaction.comment, 'reference')}
                    title={i18n.getMessage('button.copy')}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>

              {transaction.purpose && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
                  <div className='detail-value with-action'>
                    <span>{formattedPurpose}</span>
                    <button
                      className='copy-button'
                      onClick={() => handleCopy(formattedPurpose, 'purpose')}
                      title={i18n.getMessage('button.copy')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
                <div className='detail-value'>
                  <span>{commission}</span>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
                <div className='detail-value with-action'>
                  <span>{transaction.transaction_number}</span>
                  <button
                    className='copy-button'
                    onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
                    title={i18n.getMessage('button.copy')}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
                <div className='detail-value'>
                  <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
                <div className='detail-value'>
                  <span>{`${createdDate.date}, ${createdDate.time}`}</span>
                </div>
              </div>

              {completedDate.date && completedDate.time && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
                  <div className='detail-value'>
                    <span>{`${completedDate.date}, ${completedDate.time}`}</span>
                  </div>
                </div>
              )}

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
                <div className='detail-value'>
                  <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
                </div>
              </div>
            </div>
          </div>

          <div className='details-right-column'>
            {/* First Section - Sender for Incoming, Recipient for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                {isOutgoingTransaction ? (
                  // OUTGOING: Show counterparty recipient name
                  transaction.recipient_name && (
                    <div className='detail-row'>
                      <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                      <div className='detail-value with-action'>
                        <span>{transaction.recipient_name}</span>
                        <button
                          className='copy-button'
                          onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                          title={i18n.getMessage('button.copy')}
                        >
                          <CopyIcon />
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  // INCOMING: Show sender name and account
                  <>
                    {transaction.sender_name && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                        <div className='detail-value with-action'>
                          <span>{transaction.sender_name}</span>
                          <button
                            className='copy-button'
                            onClick={() => handleCopy(transaction.sender_name, 'senderName')}
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                    {(transaction.counterparty?.account_number || transaction.sender_wallet_number) && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                        <div className='detail-value break-all'>
                          <span>{transaction.counterparty?.account_number || transaction.sender_wallet_number}</span>
                          <button
                            className='copy-button'
                            onClick={() =>
                              handleCopy(
                                transaction.counterparty?.account_number || transaction.sender_wallet_number,
                                'senderAccount'
                              )
                            }
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                {isOutgoingTransaction ? (
                  // OUTGOING: Show own account details
                  <>
                    {(transaction.iban || transaction.wallet_number) && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                        <div className='detail-value break-all'>
                          <span>Card account ({transaction.iban || transaction.wallet_number})</span>
                          <button
                            className='copy-button'
                            onClick={() => handleCopy(transaction.iban || transaction.wallet_number, 'senderAccount')}
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                    {transaction.sender_name && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                        <div className='detail-value with-action'>
                          <span>{transaction.sender_name}</span>
                          <button
                            className='copy-button'
                            onClick={() => handleCopy(transaction.sender_name, 'senderName')}
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // INCOMING: Show own account details (recipient)
                  <>
                    {(transaction.iban || transaction.wallet_number) && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                        <div className='detail-value break-all'>
                          <span>{transaction.iban || transaction.wallet_number}</span>
                          <button
                            className='copy-button'
                            onClick={() =>
                              handleCopy(transaction.iban || transaction.wallet_number, 'recipientAccount')
                            }
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                    {transaction.recipient_name && (
                      <div className='detail-row'>
                        <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                        <div className='detail-value with-action'>
                          <span>{transaction.recipient_name}</span>
                          <button
                            className='copy-button'
                            onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                            title={i18n.getMessage('button.copy')}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='action-buttons'>
          <Button
            type='secondary'
            size='large'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.exportPdf')}
          </Button>

          {transaction.transaction_type === TYPES_OF_TRANSACTION.NORMAL &&
            transaction.type === TRANSACTION_TYPES.OUTGOING && (
              <Button type='primary' size='large' onClick={repeatTransaction} className='repeat-button'>
                <RepeatIcon className='button-icon' />
                {i18n.getMessage('button.repeatPayment')}
              </Button>
            )}
        </div>
      </div>
    </div>
  );
};

CardPaymentDetails.propTypes = {
  transaction: PropTypes.object.isRequired,
  amountDetails: PropTypes.object,
  commissionDetails: PropTypes.object,
  totalAmountDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject,
  transactionStore: MobXPropTypes.observableObject,
  customerPermissions: PropTypes.array,
  accountNumber: PropTypes.string
};

export default inject((stores) => ({
  accountNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore?.userAccounts,
  paymentStore: stores.paymentStore,
  transactionStore: stores.transactionStore,
  customerPermissions: stores.userStore?.customerPermissions,
  userStore: stores.userStore
}))(observer(CardPaymentDetails));
